import type { ModalProps } from '/#/components/base/modal'
import type { Ref } from 'vue'
import type {
  RouteLocation,
  RouteLocationNamedRaw,
  RouteLocationPathRaw,
} from 'vue-router'

import isFunction from 'lodash-es/isFunction'
import isString from 'lodash-es/isString'
import last from 'lodash-es/last'
import { defineStore } from 'pinia'

import { useEventDistributor } from '/@/composable/useEventDistributor'
import { routerModalClosed } from '/@/constants/bus-keys'
import { store } from '/@/store'
import { useRouter } from '/@/store/router'
type RouterModalTypes = string | RouteLocationPathRaw | RouteLocationNamedRaw

export const useRouterModalStore = defineStore('routerModal', () => {
  const router = useRouter()
  const modalName = ref('')
  const modalPropList: Ref<ModalProps> = ref({})
  const isModalOpen: Ref<boolean> = ref(false)
  // Which route will be opened in modal.
  const nextRoute: Ref<RouteLocation | undefined> = shallowRef(undefined)
  // Which component will be opened in modal.
  const modalComponent = shallowRef()
  // Previous route.
  const backgroundView = ref()

  // Event distributor for modal close events
  const { send: sendModalCloseEvent } = useEventDistributor(routerModalClosed)

  function openModal() {
    isModalOpen.value = true
  }

  function closeModal() {
    isModalOpen.value = false
  }

  /**
   * This function set the modal title name
   * @param value {modalName}
   */
  function setRouteName(value: string) {
    modalName.value = value
  }

  /**
   * This function set the modal icon name
   * @param value {modalIconName}
   */
  function setModalProps(props: ModalProps) {
    modalPropList.value = props
  }

  function setNextRoute(value: RouteLocation | undefined) {
    nextRoute.value = value
  }

  /**
   * Set backgroundView.
   * If any route opened in modal, set as previous route.
   * @param value
   */
  function setBackgroundView(value: String | undefined) {
    backgroundView.value = value
  }

  /**
   * Resolve last component from to.
   * It resolves last component from to, And we open in modal.
   * @param resolved
   */
  function setLastComponent(resolved: RouteLocation) {
    const lastComponent = last(resolved.matched)?.components?.default
    if (!lastComponent) return

    if (isFunction(lastComponent)) {
      // @ts-ignore
      // It means, target component never loaded yet. Load async.
      // TODO: update async loader component
      modalComponent.value = defineAsyncComponent(lastComponent)
    } else {
      // It means, target component loaded. Load sync.
      modalComponent.value = lastComponent
    }
  }

  function closeRouteInModal() {
    router.go(-1)
    closeModal()
    setNextRoute(undefined)
    // Send modal close event
    sendModalCloseEvent({})
  }

  /**
   * Open any router in the modal.
   * @param to - Which router will be opened.
   */
  async function openRouteInModal(to: RouterModalTypes, modalProps?: ModalProps) {
    modalPropList.value = {}
    // It means any route already opened with modal.
    // We don't want that this operation works recursively.
    // So We restore opened route and close the modal.
    if (isString(to)) to = { name: to }

    if (isModalOpen.value) {
      closeModal()
      setNextRoute(undefined)
    } else {
      if (modalProps) {
        setModalProps(modalProps)
      }
      // It means we can open the route in the modal.
      openModal()

      setBackgroundView(router.currentRoute.value.fullPath)
      const resolved = router.resolve(to)

      setLastComponent(resolved)
      setNextRoute(resolved)
    }
    await router.push(to)
  }

  /**
   * @description this method closes the modal if the route is opened at modal
   * or the route redirects to the desired location
   * @param route desired location
   */
  async function closeModalOrOpenRoute(route: RouterModalTypes | string) {
    if (isString(route)) route = { name: route }
    if (!isModalOpen.value) {
      await router.push(route)
    } else {
      closeRouteInModal()
    }
  }

  return {
    nextRoute,
    modalName,
    modalPropList,
    isModalOpen,
    modalComponent,
    backgroundView,
    openModal,
    openRouteInModal,
    closeModal,
    closeRouteInModal,
    setRouteName,
    setNextRoute,
    setBackgroundView,
    closeModalOrOpenRoute,
  } as const
})

// Need to be used outside the setup
export function useRouterModalStoreWithout() {
  return useRouterModalStore(store)
}
